import React, { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { PaymentStatusBadge } from "@/components/PaymentStatusBadge";
import { formatINR, formatDateTime } from "@/lib/format";
import { Loader2, Search, CreditCard } from "lucide-react";

const STATUSES = ["ALL", "Pending", "Processing", "Paid", "Failed", "Cancelled", "Expired"];

export default function AdminPayments() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState("ALL");
  const [q, setQ] = useState("");

  const load = useCallback(async (st) => {
    const { data } = await api.get("/admin/payments", { params: { status: st } });
    setRows(data);
    setLoading(false);
  }, []);

  useEffect(() => { load(status); }, [status, load]);

  const filtered = rows.filter((r) => {
    if (!q) return true;
    const hay = `${r.reference_number} ${r.customer_name} ${r.customer_email} ${r.order_id} ${r.payment_reference || ""}`.toLowerCase();
    return hay.includes(q.toLowerCase());
  });

  if (loading) return <div className="grid place-items-center py-20"><Loader2 className="h-8 w-8 animate-spin text-brand" /></div>;

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-2">
        <CreditCard className="h-6 w-6 text-brand" />
        <div>
          <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight">Processing Fee Payments</h1>
          <p className="text-muted-foreground text-sm">Processing-fee payment orders and their verified status.</p>
        </div>
      </div>

      <Card className="p-5 ip-card-shadow">
        <div className="flex flex-col sm:flex-row gap-3 mb-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input data-testid="payments-search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search reference, customer, order ID or payment reference" className="pl-9" />
          </div>
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger className="sm:w-48" data-testid="payments-status-filter"><SelectValue /></SelectTrigger>
            <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s === "ALL" ? "All statuses" : s}</SelectItem>)}</SelectContent>
          </Select>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-muted-foreground border-b border-border">
                <th className="py-2.5 pr-4 font-medium">Application</th>
                <th className="py-2.5 pr-4 font-medium">Customer</th>
                <th className="py-2.5 pr-4 font-medium">Amount</th>
                <th className="py-2.5 pr-4 font-medium hidden md:table-cell">Gateway</th>
                <th className="py-2.5 pr-4 font-medium hidden lg:table-cell">Gateway Order ID</th>
                <th className="py-2.5 pr-4 font-medium hidden md:table-cell">Reference</th>
                <th className="py-2.5 pr-4 font-medium hidden sm:table-cell">Created</th>
                <th className="py-2.5 pr-4 font-medium hidden lg:table-cell">Paid</th>
                <th className="py-2.5 pr-4 font-medium">Status</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && <tr><td colSpan={9} className="py-8 text-center text-muted-foreground">No payments found.</td></tr>}
              {filtered.map((p) => (
                <tr key={p.order_id} className="border-b border-border/60" data-testid={`payment-row-${p.order_id}`}>
                  <td className="py-3 pr-4 font-mono font-semibold">{p.reference_number}</td>
                  <td className="py-3 pr-4"><div className="font-medium">{p.customer_name}</div><div className="text-xs text-muted-foreground">{p.customer_email}</div></td>
                  <td className="py-3 pr-4 font-mono">{formatINR(p.amount)}</td>
                  <td className="py-3 pr-4 hidden md:table-cell capitalize">{p.gateway || "cashfree"}</td>
                  <td className="py-3 pr-4 font-mono text-xs hidden lg:table-cell">{p.rzp_order_id || p.cf_order_id || p.order_id}</td>
                  <td className="py-3 pr-4 font-mono text-xs hidden md:table-cell">{p.payment_reference || "-"}</td>
                  <td className="py-3 pr-4 text-muted-foreground hidden sm:table-cell">{formatDateTime(p.created_at)}</td>
                  <td className="py-3 pr-4 text-muted-foreground hidden lg:table-cell">{p.paid_at ? formatDateTime(p.paid_at) : "-"}</td>
                  <td className="py-3 pr-4"><PaymentStatusBadge status={p.status} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
